import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  createCheckoutSession,
  getCheckoutSessionStatus,
  getShippingCheckoutStatus,
} from '../../services/api';
import { findBundleById, findBundleByTokens } from '../../constants/tokenBundles';
import { auth } from '../../services/firebase';
import { useAuth } from '../auth';
import { useToast } from '../toast';
import { logTokenPurchase, logLockerShipment } from '../../services/analytics';

const CheckoutContext = createContext({
  tokenCheckoutContext: null,
  shippingCheckoutContext: null,
  shippingRewardModal: null,
  optimisticTokensSpent: 0,
  tokenSpendEvent: null,
  tokenGainEvent: null,
  beginTokenCheckout: async () => {},
  dismissTokenCheckout: () => {},
  showShippingRewardModal: () => {},
  dismissShippingRewardModal: () => {},
  registerTokenSpend: () => {},
  revertTokenSpend: () => {},
  queueTokenGain: () => {},
});

const resolveShippingRewardTokens = (shippingFee) => {
  const fee = Number(shippingFee);
  return Number.isFinite(fee) && fee > 0 ? 1 : 2;
};

const CheckoutProvider = ({ children }) => {
  const {
    user,
    authReady,
    requireLogin,
    resendVerification,
    refreshUser,
  } = useAuth();
  const { showToast, dismissToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  const [tokenCheckoutContext, setTokenCheckoutContext] = useState(null);
  const [shippingCheckoutContext, setShippingCheckoutContext] = useState(null);
  const [shippingRewardModal, setShippingRewardModal] = useState(null);
  const [optimisticTokensSpent, setOptimisticTokensSpent] = useState(0);
  const [tokenSpendEvent, setTokenSpendEvent] = useState(null);
  const [tokenGainEvent, setTokenGainEvent] = useState(null);

  const processedCheckoutSessionRef = useRef(null);
  const processedShippingSessionRef = useRef(null);
  const tokenGainQueueRef = useRef([]);
  const tokenGainTimerRef = useRef(null);
  const previousUserIdRef = useRef(user?.id ?? null);
  const previousUserTokensRef = useRef(
    typeof user?.tokens === 'number' ? user.tokens : 0
  );

  const resetTokenGainState = useCallback(() => {
    if (tokenGainTimerRef.current) {
      clearTimeout(tokenGainTimerRef.current);
      tokenGainTimerRef.current = null;
    }
    tokenGainQueueRef.current = [];
    setTokenGainEvent(null);
  }, []);

  useEffect(() => {
    return () => {
      resetTokenGainState();
    };
  }, [resetTokenGainState]);

  useEffect(() => {
    const currentUserId = user?.id ?? null;
    if (previousUserIdRef.current !== currentUserId) {
      previousUserIdRef.current = currentUserId;
      setOptimisticTokensSpent(0);
      setTokenSpendEvent(null);
      resetTokenGainState();
      previousUserTokensRef.current =
        typeof user?.tokens === 'number' ? user.tokens : 0;
    }
  }, [user, resetTokenGainState]);

  useEffect(() => {
    const currentTokens =
      typeof user?.tokens === 'number' ? user.tokens : 0;
    const previousTokens = previousUserTokensRef.current;
    if (
      typeof previousTokens === 'number' &&
      currentTokens < previousTokens &&
      optimisticTokensSpent > 0
    ) {
      const settled = Math.min(
        previousTokens - currentTokens,
        optimisticTokensSpent
      );
      if (settled > 0) {
        setOptimisticTokensSpent((prev) => Math.max(prev - settled, 0));
      }
    }
    previousUserTokensRef.current = currentTokens;
  }, [user?.tokens, optimisticTokensSpent]);

  const flushTokenGainQueue = useCallback(() => {
    if (tokenGainTimerRef.current) {
      clearTimeout(tokenGainTimerRef.current);
      tokenGainTimerRef.current = null;
    }
    const [next] = tokenGainQueueRef.current;
    if (!next) {
      resetTokenGainState();
      return;
    }
    setTokenGainEvent({
      amount: next.amount,
      id: Date.now(),
    });
    tokenGainQueueRef.current = tokenGainQueueRef.current.slice(1);
    tokenGainTimerRef.current = setTimeout(() => {
      flushTokenGainQueue();
    }, 300);
  }, [resetTokenGainState]);

  const queueTokenGain = useCallback(
    (amount) => {
      const parsed = Number(amount);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        return;
      }
      tokenGainQueueRef.current = [
        ...tokenGainQueueRef.current,
        { amount: parsed },
      ];
      if (!tokenGainTimerRef.current) {
        flushTokenGainQueue();
      }
    },
    [flushTokenGainQueue],
  );

  const registerTokenSpend = useCallback((amount) => {
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return;
    }
    setOptimisticTokensSpent((prev) => prev + parsed);
    setTokenSpendEvent({
      amount: parsed,
      id: Date.now(),
    });
  }, []);

  const revertTokenSpend = useCallback((amount) => {
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return;
    }
    setOptimisticTokensSpent((prev) => Math.max(prev - parsed, 0));
  }, []);

  const dismissTokenCheckout = useCallback(() => {
    processedCheckoutSessionRef.current = null;
    setTokenCheckoutContext(null);
  }, []);

  const dismissShippingRewardModal = useCallback(() => {
    setShippingRewardModal(null);
  }, []);

  const showShippingRewardModal = useCallback(({
    shippingFee = 0,
    tokensAwarded = null,
    tokenBalance = null,
    shipmentId = null,
    rewardKind = 'shipping',
  } = {}) => {
    const parsedTokens = Number(tokensAwarded);
    const rewardTokens =
      Number.isFinite(parsedTokens) && parsedTokens > 0
        ? parsedTokens
        : resolveShippingRewardTokens(shippingFee);
    setShippingRewardModal({
      id: `${shipmentId || 'shipment'}-${Date.now()}`,
      tokensAwarded: rewardTokens,
      tokenBalance:
        Number.isFinite(Number(tokenBalance)) ? Number(tokenBalance) : null,
      shippingFee,
      shipmentId,
      rewardKind,
    });
  }, []);

  const beginTokenCheckout = useCallback(
    async (bundle) => {
      if (!user) {
        requireLogin?.('Sign in to purchase tokens.', 'signup');
        throw new Error('Authentication required');
      }

      if (auth.currentUser && !auth.currentUser.emailVerified) {
        showToast({
          message: (
            <>
              Verify your email to unlock all features.&nbsp;
              <button
                type="button"
                onClick={resendVerification}
                className="font-semibold underline underline-offset-2 hover:opacity-80"
              >
                Resend.
              </button>
            </>
          ),
          tone: 'info',
          duration: 0,
        });
        throw new Error('Email not verified');
      }

      const targetBundle =
        (bundle && bundle.id && bundle) ||
        (bundle?.bundleId && findBundleById(bundle.bundleId)) ||
        (bundle?.tokens && findBundleByTokens(bundle.tokens)) ||
        null;

      if (!targetBundle) {
        const message = 'Invalid token bundle selected.';
        showToast({ message, tone: 'error' });
        throw new Error(message);
      }

      try {
        const result = await createCheckoutSession({ bundleId: targetBundle.id });
        if (result?.checkoutUrl) {
          window.location.assign(result.checkoutUrl);
          return;
        }
        throw new Error('Checkout session did not return a redirect URL.');
      } catch (error) {
        const message =
          error?.response?.data?.error ||
          error?.message ||
          'Unable to open checkout right now. Please try again later.';
        showToast({ message, tone: 'error' });
        throw Object.assign(new Error(message), { cause: error, response: error?.response });
      }
    },
    [user, requireLogin, showToast, resendVerification],
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasCheckout = params.has('checkout');
    const hasShipping = params.has('shipping');
    let needsCleanup = false;

    if (hasCheckout) {
      const rawStatus = params.get('checkout') || '';
      const normalizedStatus = ['success', 'cancel'].includes(rawStatus) ? rawStatus : 'error';
      const sessionId = params.get('session_id') || null;
      const bundleId = params.get('bundleId') || null;
      setTokenCheckoutContext({
        status: normalizedStatus,
        sessionId,
        bundleId,
        id: Date.now(),
      });
      params.delete('checkout');
      params.delete('session_id');
      params.delete('bundleId');
      needsCleanup = true;
    }

    if (hasShipping) {
      const rawStatus = params.get('shipping') || '';
      const normalizedStatus = ['success', 'cancel'].includes(rawStatus) ? rawStatus : 'error';
      const sessionId = params.get('shipping_session_id') || params.get('session_id') || null;
      setShippingCheckoutContext({
        status: normalizedStatus,
        sessionId,
        id: Date.now(),
        resolved: normalizedStatus !== 'success',
      });
      params.delete('shipping');
      params.delete('shipping_session_id');
      if (params.get('session_id')) {
        params.delete('session_id');
      }
      needsCleanup = true;
    }

    if (needsCleanup) {
      const search = params.toString();
      navigate(
        { pathname: location.pathname, search: search ? `?${search}` : '' },
        { replace: true },
      );
    }
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (
      !authReady ||
      !tokenCheckoutContext ||
      tokenCheckoutContext.status !== 'success' ||
      !tokenCheckoutContext.sessionId ||
      tokenCheckoutContext.resolved
    ) {
      return;
    }

    let isActive = true;
    let timeoutId = null;
    const targetSessionId = tokenCheckoutContext.sessionId;
    const maxAttempts = 6;

    const poll = async (attempt = 0) => {
      try {
        const result = await getCheckoutSessionStatus(targetSessionId);
        if (!isActive) {
          return;
        }

        if (result.status === 'completed' || result.status === 'success') {
          setTokenCheckoutContext((current) => {
            if (!current || current.sessionId !== targetSessionId) {
              return current;
            }
            const bundle = current.purchase?.bundleId
              ? findBundleById(current.purchase.bundleId)
              : current.bundleId
              ? findBundleById(current.bundleId)
              : null;
            return {
              ...current,
              resolved: true,
              status: 'success',
              purchase: result.purchase || current.purchase || null,
              tokens: result.purchase?.tokens || bundle?.tokens || current.tokens || null,
              user: result.user || current.user || null,
            };
          });
          return;
        }

        if (result.status === 'pending' && attempt < maxAttempts) {
          timeoutId = setTimeout(() => poll(attempt + 1), 1500);
          return;
        }

        if (result.status === 'pending') {
          const message = 'We are still confirming your payment. Tokens will appear shortly.';
          setTokenCheckoutContext((current) => {
            if (!current || current.sessionId !== targetSessionId) {
              return current;
            }
            return {
              ...current,
              status: 'pending',
              resolved: true,
              message,
            };
          });
          showToast({ message, tone: 'info' });
          return;
        }

        const fallbackMessage =
          result?.message ||
          'We could not confirm the payment. Please check your balance before retrying.';
        setTokenCheckoutContext((current) => {
          if (!current || current.sessionId !== targetSessionId) {
            return current;
          }
          return {
            ...current,
            status: 'error',
            resolved: true,
            message: fallbackMessage,
          };
        });
        showToast({ message: fallbackMessage, tone: 'error' });
      } catch (error) {
        if (!isActive) {
          return;
        }
        console.error('Failed to confirm checkout session:', error);
        if (attempt < 3) {
          timeoutId = setTimeout(() => poll(attempt + 1), 1500);
          return;
        }
        const message =
          error?.response?.data?.error ||
          error?.message ||
          'Unable to verify payment status. Please contact support if tokens do not appear.';
        setTokenCheckoutContext((current) => {
          if (!current || current.sessionId !== targetSessionId) {
            return current;
          }
          return {
            ...current,
            status: 'error',
            resolved: true,
            message,
          };
        });
        showToast({ message, tone: 'error' });
      }
    };

    poll();

    return () => {
      isActive = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [tokenCheckoutContext, authReady, showToast]);

  useEffect(() => {
    if (
      !tokenCheckoutContext ||
      tokenCheckoutContext.status !== 'success' ||
      !tokenCheckoutContext.resolved
    ) {
      return;
    }

    const sessionKey =
      tokenCheckoutContext.sessionId ||
      tokenCheckoutContext.id ||
      tokenCheckoutContext.purchase?.id ||
      null;

    const tokensAwarded = Number(tokenCheckoutContext.tokens);

    if (
      !sessionKey ||
      processedCheckoutSessionRef.current === sessionKey ||
      !Number.isFinite(tokensAwarded) ||
      tokensAwarded <= 0
    ) {
      return;
    }

    processedCheckoutSessionRef.current = sessionKey;

    // CHANGED: Log analytics event for token purchase
    const bundle = tokenCheckoutContext.purchase?.bundleId
      ? findBundleById(tokenCheckoutContext.purchase.bundleId)
      : tokenCheckoutContext.bundleId
      ? findBundleById(tokenCheckoutContext.bundleId)
      : null;
    if (bundle && tokenCheckoutContext.purchase) {
      // Use actual payment amount (amountTotal in cents) if available,
      // otherwise fall back to bundle price * 100
      const valueInCents =
        Number(tokenCheckoutContext.purchase.amountTotal) ||
        bundle.price * 100;
      logTokenPurchase(bundle.id, tokensAwarded, valueInCents);
    }

    refreshUser?.().catch(() => {});
    queueTokenGain(tokensAwarded);
    setOptimisticTokensSpent(0);
    dismissToast();
  }, [tokenCheckoutContext, refreshUser, queueTokenGain, dismissToast]);

  useEffect(() => {
    if (
      !authReady ||
      !shippingCheckoutContext ||
      shippingCheckoutContext.status !== 'success' ||
      !shippingCheckoutContext.sessionId ||
      shippingCheckoutContext.resolved
    ) {
      return;
    }

    let isActive = true;
    let timeoutId = null;
    const targetSessionId = shippingCheckoutContext.sessionId;
    const maxAttempts = 6;

    const poll = async (attempt = 0) => {
      try {
        const result = await getShippingCheckoutStatus(targetSessionId);
        if (!isActive) {
          return;
        }

        if (result.status === 'completed' || result.status === 'success') {
          setShippingCheckoutContext((current) => {
            if (!current || current.sessionId !== targetSessionId) {
              return current;
            }
            return {
              ...current,
              resolved: true,
              status: 'success',
              shipment: result.shipment || current.shipment || null,
              checkout: result.checkout || current.checkout || null,
              user: result.user || current.user || null,
            };
          });
          return;
        }

        if (attempt + 1 < maxAttempts) {
          timeoutId = setTimeout(() => poll(attempt + 1), 1500);
        } else {
          setShippingCheckoutContext((current) => {
            if (!current || current.sessionId !== targetSessionId) {
              return current;
            }
            return {
              ...current,
              resolved: true,
              status: result.status || 'pending',
            };
          });
        }
      } catch (error) {
        console.error('Failed to confirm shipping checkout session:', error);
        if (!isActive) {
          return;
        }
        setShippingCheckoutContext((current) => {
          if (!current || current.sessionId !== targetSessionId) {
            return current;
          }
          const message =
            error?.response?.data?.error ||
            error?.message ||
            'Unable to verify shipping payment status. Please contact support if this persists.';
          return {
            ...current,
            resolved: true,
            status: 'error',
            error: message,
          };
        });
      }
    };

    poll();

    return () => {
      isActive = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [shippingCheckoutContext, authReady]);

  useEffect(() => {
    if (!shippingCheckoutContext || !shippingCheckoutContext.resolved) {
      return;
    }

    const sessionKey =
      shippingCheckoutContext.sessionId || shippingCheckoutContext.id || null;
    if (sessionKey && processedShippingSessionRef.current === sessionKey) {
      return;
    }

    if (sessionKey) {
      processedShippingSessionRef.current = sessionKey;
    }

    if (shippingCheckoutContext.status === 'success') {
      // CHANGED: Log analytics event for locker shipment (paid shipping)
      const shipment = shippingCheckoutContext.shipment;
      const itemCount = shipment?.items?.length || shipment?.itemCount || 0;
      const shippingFee =
        shipment?.shippingFee ??
        shipment?.fee ??
        shippingCheckoutContext.checkout?.shippingFee ??
        0;
      logLockerShipment(itemCount, shippingFee);
      refreshUser?.().catch(() => {});
      showShippingRewardModal({
        shippingFee,
        tokensAwarded: resolveShippingRewardTokens(shippingFee),
        tokenBalance: shippingCheckoutContext.user?.tokens,
        shipmentId: shipment?.id || shippingCheckoutContext.checkout?.shipmentId,
      });
    }
  }, [shippingCheckoutContext, refreshUser, showShippingRewardModal]);

  const value = useMemo(
    () => ({
      tokenCheckoutContext,
      shippingCheckoutContext,
      shippingRewardModal,
      optimisticTokensSpent,
      tokenSpendEvent,
      tokenGainEvent,
      beginTokenCheckout,
      dismissTokenCheckout,
      showShippingRewardModal,
      dismissShippingRewardModal,
      registerTokenSpend,
      revertTokenSpend,
      queueTokenGain,
    }),
    [
      tokenCheckoutContext,
      shippingCheckoutContext,
      shippingRewardModal,
      optimisticTokensSpent,
      tokenSpendEvent,
      tokenGainEvent,
      beginTokenCheckout,
      dismissTokenCheckout,
      showShippingRewardModal,
      dismissShippingRewardModal,
      registerTokenSpend,
      revertTokenSpend,
      queueTokenGain,
    ],
  );

  return <CheckoutContext.Provider value={value}>{children}</CheckoutContext.Provider>;
};

export const useCheckout = () => {
  return useContext(CheckoutContext);
};

export default CheckoutProvider;
